/**
 * Start a new conversation — `POST messaging/conversations/`. The caller is
 * always added as a participant server-side even if they don't include their
 * own id, so this form only needs the OTHER participant(s).
 *
 * Plain numeric user-id input, comma-separated — same "no picker widget"
 * precedent as the assigned-agent field elsewhere in this app. Validation of
 * who the caller may message lives entirely server-side
 * (`allowedContactIds`); this form does not attempt to reconstruct that rule,
 * it just surfaces the backend's 403 message on failure.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createConversation } from '../../api/endpoints/messaging';

export function NewConversationPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [participantIds, setParticipantIds] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const participants = participantIds
        .split(',')
        .map((value) => value.trim())
        .filter((value) => value !== '')
        .map((value) => Number(value));
      return createConversation({ participants });
    },
    onSuccess: (conversation) => navigate(`../${conversation.id}`, { replace: true }),
    onError: (caught) => setError(parseApiError(caught).message),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    create.mutate();
  }

  return (
    <section>
      <h2>New conversation</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="conversation-participants">Participant user ids (comma-separated)</label>
        <input
          id="conversation-participants"
          required
          value={participantIds}
          onChange={(event) => setParticipantIds(event.target.value)}
        />

        <button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Starting…' : 'Start conversation'}
        </button>
      </form>
    </section>
  );
}

export default NewConversationPage;
